import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';

import CompassIcon from 'Compass/components/icons/CompassIcon';
import { useUserContext } from 'Compass//contexts/UserContext';
import { DesktopShow, DeviceQueries, MobileShow } from 'Common/components/styled';
import Dropdown, { DropdownDivider, DropdownItem } from 'Compass/components/Dropdown';
import { useModalContext } from 'Common/contexts/ModalContext';
import LogoutModal from 'Compass/components/modals/LogoutModal';

export enum PageType {
  Orders = 1,
  PromptEditor,
  Dashboard,
  ProfileLab,
  Suggestions,
  IdeaLab,
  Revere,
  UserView,
  Eval,
  ClusteringLab
}

const CompassHeader = ({ pageType }: { pageType?: PageType }) => {
  const navigate = useNavigate();
  const userContext = useUserContext();
  const modalContext = useModalContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    modalContext.show({
      contents: <LogoutModal close={modalContext.hide} />
    });
    setIsMenuOpen(false)
  }

  return <HeaderContainer role="banner">
    <HeaderSection role="navigation">
      <IconWrapper onClick={() => navigate('/')}>
        <CompassIcon color="white" />
      </IconWrapper>
      <DesktopShow>
        <HeaderItem $current={pageType === PageType.Suggestions} onClick={() => navigate('/suggestions')}>
          Suggestions
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.Orders} onClick={() => navigate('/orders')}>
          Orders
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.PromptEditor} onClick={() => navigate('/prompt')}>
          Prompt Editor
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.Dashboard} onClick={() => navigate('/dashboard')}>
          Dashboard
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.ProfileLab} onClick={() => navigate('/profile-lab')}>
          Profile Lab
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.IdeaLab} onClick={() => navigate('/idea-lab')}>
          Idea Lab
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.Revere} onClick={() => navigate('/revere')}>
          Revere
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.UserView} onClick={() => navigate('/user-view')}>
          User View
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.Eval} onClick={() => navigate('/eval')}>
          Eval
        </HeaderItem>
        <HeaderItem $current={pageType === PageType.ClusteringLab} onClick={() => navigate('/clustering-lab')}>
          Clustering Lab
        </HeaderItem>
      </DesktopShow>
    </HeaderSection>
    <HeaderSection>
      <DesktopShow>
        <HeaderItem onClick={handleLogout}>
          {userContext.name}
        </HeaderItem>
      </DesktopShow>
      <MobileShow>
        <HeaderItem>
          {userContext.name}
        </HeaderItem>
        <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <MenuIcon>
            <span />
            <span />
            <span />
          </MenuIcon>
        </MenuButton>
      </MobileShow>
    </HeaderSection>
    {isMenuOpen && (
      <Dropdown onClose={() => setIsMenuOpen(false)}>
        <DropdownItem $current={pageType === PageType.Orders} onClick={() => handleNavigate('/orders')}>
          Orders
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.PromptEditor} onClick={() => handleNavigate('/prompt')}>
          Prompt Editor
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.Dashboard} onClick={() => handleNavigate('/dashboard')}>
          Dashboard
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.IdeaLab} onClick={() => handleNavigate('/idea-lab')}>
          Idea Lab
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.Suggestions} onClick={() => handleNavigate('/suggestions')}>
          Suggestions
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.ProfileLab} onClick={() => handleNavigate('/profile-lab')}>
          Profile Lab
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.Revere} onClick={() => handleNavigate('/revere')}>
          Revere
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.Eval} onClick={() => handleNavigate('/eval')}>
          Eval
        </DropdownItem>
        <DropdownItem $current={pageType === PageType.ClusteringLab} onClick={() => handleNavigate('/clustering-lab')}>
          Clustering Lab
        </DropdownItem>
        <DropdownDivider />
        <DropdownItem $current={pageType === PageType.Dashboard} onClick={handleLogout}>
          Log out
        </DropdownItem>
      </Dropdown>
    )}
  </HeaderContainer>;
};

const HeaderContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  min-height: 56px;
  background: linear-gradient(135deg, #02033B 0%, #743D98 60%, #AC326C 100%);
  box-shadow: 0 2px 12px rgba(2, 3, 59, 0.2);
  padding: 0 24px;

  @media (${DeviceQueries.mobile}) {
    padding: 0 16px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

const IconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  margin-right: 8px;
`;

const HeaderItem = styled.div<{ $current?: boolean }>`
  color: ${({ $current }) => $current ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'};
  font-weight: ${({ $current }) => $current ? '600' : '400'};
  font-size: 13px;
  letter-spacing: 0.2px;
  padding: 6px 14px;
  border-radius: 8px;
  background: ${({ $current }) => $current ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  display: flex;
  align-items: center;
  white-space: nowrap;

  &:hover {
    background: ${({ $current }) => $current ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
    color: #ffffff;
  }

  &:active {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MenuIcon = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 24px;

  span {
    display: block;
    width: 100%;
    height: 2px;
    background: white;
    border-radius: 2px;
    transition: all 0.3s ease;
  }
`;

export default CompassHeader;
